import React, { useState, useEffect, useRef } from 'react'
import {
    EyeIcon,
    ArrowLeftIcon,
    PencilIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    CheckCircleIcon,
    WifiIcon,
    InformationCircleIcon,
    FunnelIcon,
    ChatBubbleBottomCenterIcon,
    PhoneIcon,
    PrinterIcon,
    EnvelopeIcon,
    UserIcon,
    PlusCircleIcon, // Add this import
    XMarkIcon,
    CheckIcon,
    DocumentIcon,
    ArrowDownTrayIcon,
} from '@heroicons/react/20/solid'
// Fix: Update Headless UI imports
import { Disclosure, Menu } from '@headlessui/react'
import { EllipsisVerticalIcon, CalendarIcon, UserIcon as OutlineUserIcon } from '@heroicons/react/24/outline'
import { BoatIcon, BoatBookingImage, MedicalIcon, PassportIcon } from 'components/icons'
import stSeat from '../../../assets/images/icons/st_seat.svg'
import vipSeat from '../../../assets/images/icons/vip_seat.svg'
import { GET_ALL_BOOKINGS } from 'graphql/booking/queries'
import { useLazyQuery } from '@apollo/client'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { t } from 'i18next'
import loadingImage from 'assets/images/boat-loading.gif'
import {
    customDateFormat,
    customDateTimeFormat,
    customTimeFormat,
    getTimeDifference,
    utcDateTimeToBahrainTime,
    getS3UrlFromKey,
} from 'utils/common'
import { AGE_GROUPS, SEAT_TYPES } from '../../constants'
import { CANCEL_BOOKING } from 'graphql/booking/mutations'
import { useMutation } from '@apollo/client'
import { Badge } from 'components'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { QRCodeCanvas } from 'qrcode.react'
import BoatBookingConformation from '../../boatBooking/confirmation'
import TicketActions from 'components/custom/TicketActions'
import i18n from 'i18next'
import PaymentInfoDialog from 'components/custom/PaymentInfoDialog'
import { ADD_BOOKING_PAYMENT_DETAILS } from 'graphql/booking/mutations'
import { UpdateBoatDialog } from '../updateBoatDialog'
import moment from 'moment'
import { UpdateBoatStatusDialog } from '../updateBoatStatusDialog'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

function formatDuration(minutes) {
    if (!minutes) return ''

    if (minutes < 60) {
        return `${minutes} ${minutes === 1 ? t('minute') : t('minutes')}`
    }

    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    if (remainingMinutes === 0) {
        return `${hours} ${hours === 1 ? t('hour') : t('hours')}`
    }

    return `${hours}${t('hr')} ${remainingMinutes}${t('mins')}`
}

function getTripStatusLabel(statusCode) {
    const statusMap = {
        ONP: 'On Trip',
        PND: 'Pending',
        CMP: 'Completed',
        CXL: 'Cancelled',
        NOS: 'No Show',
        TRC: 'Trip Cancelled',
        PRC: 'Processing Payment',
        CNF: 'Confirmed',
        RSC: 'Rescheduled',
        REV: 'Under Review - Admin checking',
    }
    return statusMap[statusCode] || statusCode
}

function getTripStatusColor(statusCode) {
    const colorMap = {
        CNF: 'green',
        ONP: 'blue',
        CMP: 'green',
        CXL: 'red',
        TRC: 'red',
        NOS: 'orange',
        PND: 'yellow',
        PRC: 'yellow',
        RSC: 'red',
        REV: 'orange',
    }
    return colorMap[statusCode] || 'zinc'
}

export default function ManageBoatBookingDetail() {
    const { booking_id, travel_date, booking_type } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const { filterObj, isCheckInMode, fromAppUsers, userEmail, userName, userId } = location.state || {}
    const [bookingInfo, setBookingInfo] = useState(null)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessages, setDialogMessages] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmDialogMessage, setConfirmDialogMessage] = useState(t('Are you sure you want to cancel this booking?'))
    const [confirmDialogTitle, setConfirmDialogTitle] = useState(t('Cancel Booking'))
    const [showPaymentDialog, setShowPaymentDialog] = useState(false)
    const [showCancellationDialog, setShowCancellationDialog] = useState(false)
    const [selectedTripIds, setSelectedTripIds] = useState([])
    const [cancellationType, setCancellationType] = useState('FULL')
    const [showDocumentPreview, setShowDocumentPreview] = useState(false)
    const [previewDocument, setPreviewDocument] = useState(null)
    const [documentUrl, setDocumentUrl] = useState(null)
    const [openUpdateDialog, setOpenUpdateDialog] = useState(false)
    const [openUpdateStatusDialog, setOpenUpdateStatusDialog] = useState(false)

    const [getBookingList, { loading: bookingListLoading, error: bookingListError }] = useLazyQuery(GET_ALL_BOOKINGS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getAllBookings.success && data.getAllBookings.code === '200') {
                const booking = data.getAllBookings.bookings[0]
                // Log to check if reschedule_allowed and trip_type are available (only for non-DYP bookings)
                if (booking?.trip_segments) {
                    console.log(
                        'Trip segments:',
                        booking.trip_segments.map(segment => ({
                            reschedule_allowed: segment.reschedule_allowed,
                            trip_type: segment.trip_type,
                        }))
                    )
                }
                setBookingInfo(booking)
            }
        },
    })
    const searchBookings = () => {
        const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
        const pastYear = new Date()
        pastYear.setFullYear(pastYear.getFullYear() - 1)
        const pastYearDate = pastYear.toISOString().split('T')[0]
        getBookingList({
            variables: {
                bookingFilter: {
                    booking_id: booking_id,
                    booking_type: booking_type,
                    search_string: null,
                    status: null,
                    from_date: pastYearDate,
                    to_date: travel_date,
                },
                page: {
                    limit: 1,
                    offset: 0,
                },
            },
        })
    }

    useEffect(() => {
        searchBookings()
    }, [booking_id])

    const handleBack = () => {
        if (fromAppUsers) {
            navigate('/app-users/bookings', { state: { userEmail, userName, userId } })
        } else if (isCheckInMode) {
            navigate('/check-in', { state: { filterObj } })
        } else if (filterObj) {
            navigate('/manage-boat-booking', { state: { filterObj } })
        } else {
            navigate('/boat-booking')
        }
    }

    //  MUtation Add Boat
    const [cancelBooking, { loading: cancelLoading }] = useMutation(CANCEL_BOOKING, {
        onCompleted: data => {
            if (data?.cancelBooking.code === '200' && data?.cancelBooking.success === true) {
                setShowSucDialog(true)
                searchBookings() // Refresh the page by calling the initial API again
            } else {
                setDialogMessages(data?.cancelBooking?.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessages(error.message)
            setShowErrDialog(true)
        },
    })

    const [addPaymentDetails, { loading: paymentLoading }] = useMutation(ADD_BOOKING_PAYMENT_DETAILS, {
        onCompleted: data => {
            if (data?.addBookingPaymentDetails.code === '200' && data?.addBookingPaymentDetails.success === true) {
                setShowSucDialog(true)
                setDialogMessages(t('Payment details added successfully'))
                setShowPaymentDialog(false)
                searchBookings() // Refresh the data
            } else {
                setDialogMessages(data?.addBookingPaymentDetails.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessages(error.message || t('Failed to add payment details'))
            setShowErrDialog(true)
        },
    })

    const handleCancelBooking = () => {
        // Check if booking has multiple trip segments for partial cancellation option
        if (bookingInfo?.trip_segments && bookingInfo.trip_segments.length > 1) {
            setShowCancellationDialog(true)
        } else {
            // Single trip segment - proceed with full cancellation
            setCancellationType('FULL')
            setSelectedTripIds([])
            setConfirmDialogMessage(t('Are you sure you want to cancel this booking?'))
            setConfirmDialogTitle(t('Cancel Booking'))
            setShowConfirmDialog(true)
        }
    }

    const handleTripSelection = tripId => {
        console.log('tripId', tripId)
        setSelectedTripIds(prev => {
            if (prev.includes(tripId)) {
                return prev.filter(id => id !== tripId)
            } else {
                return [...prev, tripId]
            }
        })
    }

    const handleCancellationTypeChange = type => {
        setCancellationType(type)
        if (type === 'FULL') {
            setSelectedTripIds([])
        }
    }

    const proceedWithCancellation = () => {
        setShowCancellationDialog(false)

        if (cancellationType === 'FULL') {
            setConfirmDialogMessage(t('Are you sure you want to cancel this entire booking?'))
            setConfirmDialogTitle(t('Cancel Entire Booking'))
        } else {
            const selectedTripsCount = selectedTripIds.length
            setConfirmDialogMessage(
                t(
                    `Are you sure you want to cancel ${selectedTripsCount} selected trip${selectedTripsCount > 1 ? 's' : ''}?`
                )
            )
            setConfirmDialogTitle(t('Cancel Selected Trips'))
        }

        setShowConfirmDialog(true)
    }

    const handleCheckIn = (segmentId, travelDate) => {
        // Navigate to check-in page with segment information for trip-level check-in
        navigate('/check-in/process', {
            state: {
                bookingInfo: bookingInfo,
                segmentId: segmentId,
                travelDate: travelDate,
                bookingId: booking_id,
            },
        })
    }

    const handlePaymentSubmit = formData => {
        const paymentInfo = {
            no: formData.receiptNumber,
            date: formData.receiptDate,
            currency: 'BHD', // Default currency
            card_no: formData.cardNo,
            card_brand: formData.card_brand, // Default card brand
            amount: parseFloat(formData.amount),
        }

        // Use first segment's travel date if available, otherwise fallback to passengers travel_date (for DYP), metadata travel_date or URL param
        const firstSegmentTravelDate =
            bookingInfo?.trip_segments?.[0]?.travel_date ||
            bookingInfo?.passengers?.[0]?.travel_date ||
            bookingInfo?.metadata?.travel_date ||
            travel_date

        addPaymentDetails({
            variables: {
                bookingId: booking_id,
                travelDate: firstSegmentTravelDate,
                paymentInfo: paymentInfo,
            },
        })
    }

    // Navigate to reschedule flow for the given segment
    const handleReschedule = segment => {
        if (bookingInfo?.status?.code === 'CXL' || segment?.trip_status === 'CXL' || !segment.reschedule_allowed) return
        navigate('/boat-booking/reschedule', {
            state: {
                bookingId: booking_id,
                bookingInfo: bookingInfo,
                segment: segment,
                tripType: segment.trip_type, // Include trip_type for the searchTrip API
            },
        })
    }

    const GoDetailsPage = (bookingId, travel_date) => {
        // Open in a new tab/window
        window.open(`/manage-boat-booking/details/${bookingId}/${travel_date}/DYP`, '_blank')
    }

    const handleViewDocument = async doc => {
        const url = await getS3UrlFromKey(doc.s3_key)
        if (url) {
            setPreviewDocument(doc)
            setDocumentUrl(url.toString())
            setShowDocumentPreview(true)
        }
    }

    const handleDownloadDocument = async doc => {
        const url = await getS3UrlFromKey(doc.s3_key)
        if (url) {
            const link = document.createElement('a')
            link.href = url.toString()
            link.download = doc.file_name
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const handlePaymentSuccess = apiMessage => {
        setDialogMessages(apiMessage || t('Payment details added successfully'))
        setShowSucDialog(true)
        setShowPaymentDialog(false) // Close the payment dialog
        searchBookings() // Refresh the data
    }

    // Determine if the booking is for a domestic route (DMC)
    const isDomesticRoute = bookingInfo?.trip_segments?.[0]?.route?.master_route?.route_type?.code === 'DMC'

    // Helper function to check if booking is Day Pass type
    const isDayPassBooking = bookingInfo?.booking_type?.code === 'DYP'

    // Helper function to get passengers for display
    const getDisplayPassengers = () => {
        if (isDayPassBooking) {
            return bookingInfo?.passengers || []
        }
        return bookingInfo?.trip_segments?.[0]?.passengers || []
    }

    // Helper function to get travel date
    const getDisplayTravelDate = () => {
        if (isDayPassBooking) {
            return bookingInfo?.passengers?.[0]?.travel_date
        }
        return bookingInfo?.trip_segments?.[0]?.travel_date || bookingInfo?.metadata?.travel_date
    }

    function isTravelDatePassed(travel_date) {
        if (!travel_date) return false

        const travelDateString = travel_date.split('T')[0]
        const todayString = new Date().toISOString().split('T')[0]

        return travelDateString < todayString
    }

    return (
        <>
            <div id="manage_page_back" className="mb-4 flex justify-normal items-center">
                <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-bold leading-7 text-primary pb-1">{t('Booking details')}</h3>
                </div>
            </div>

            {bookingListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {bookingListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch boat!')}</div>
                </div>
            )}
            {!bookingListLoading && !bookingListError && !bookingInfo && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('No Booking found!')}</div>
                </div>
            )}
            {bookingInfo && !bookingListLoading && (
                <>
                    <div id="manage_page" className="grid grid-col-1 md:grid-cols-6 gap-x-4">
                        <div
                            className={` flex flex-col ${bookingInfo?.trip_segments && !isDayPassBooking ? 'col-span-4' : 'col-span-6'}`}
                        >
                            <div className="bg-white relative rounded-lg border-[1px] border-solid border-slate-300 p-6 mt-5 pt-4 pb-4">
                                {(bookingInfo?.booking_type || bookingInfo?.metadata) && (
                                    <p className="text-primary whitespace-nowrap bg-primary/20 absolute top-0 right-0 left-0 mx-auto text-sm italic font font-bold text-center rounded-b-md w-fit p-2 px-3 pt-1">
                                        {bookingInfo.booking_type?.description[i18n?.language || 'en']}
                                        {bookingInfo.metadata?.booking_type?.description
                                            ? ' with ' +
                                              bookingInfo?.metadata?.booking_type.description[i18n?.language || 'en']
                                            : ''}
                                    </p>
                                )}
                                <div className="mb-4 flex justify-between flex-col lg:flex-row gap-y-6 lg:gap-y-0">
                                    <div className="flex items-center gap-x-4">
                                        <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74] rounded-full bg-primary" />
                                        <div className="min-w-0 flex-1">
                                            <h2 className="text-xl font-bold break-words">
                                                {isDayPassBooking
                                                    ? t('Day Pass')
                                                    : bookingInfo?.trip_segments
                                                      ? bookingInfo.trip_segments[0].route?.boat?.name?.en
                                                      : 'N/A'}
                                            </h2>
                                            <p className="text-[14px] text-gray-700 sm:mt-2 break-words">
                                                {isDayPassBooking ? (
                                                    <>
                                                        <span className="font-bold pe-1">
                                                            {bookingInfo?.passengers?.length || 0}
                                                        </span>
                                                        {t('Passengers')}
                                                        {bookingInfo?.passengers?.[0]?.seat_info?.seat_type_code && (
                                                            <span className="ms-2">
                                                                | {bookingInfo.passengers[0].seat_info.seat_type_code}{' '}
                                                                {t('Seat')}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {bookingInfo?.trip_segments
                                                            ? bookingInfo.trip_segments[0].trip?.trip_name?.en
                                                            : 'N/A'}{' '}
                                                        |{' '}
                                                        <span className="font-bold pe-1">
                                                            {bookingInfo?.trip_segments
                                                                ? bookingInfo.trip_segments[0].passengers.length
                                                                : '0 '}
                                                        </span>
                                                        {t('Passengers')}
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-end flex flex-col justify-end">
                                        <p className="text-sm text-gray-600">
                                            {t('Booking Status')}
                                            {' : '}
                                            <Badge
                                                color={
                                                    bookingInfo.status?.description?.en === 'Confirmed'
                                                        ? 'emerald'
                                                        : bookingInfo.status?.description?.en === 'Payment Processing'
                                                          ? 'lime'
                                                          : bookingInfo.status?.description?.en === 'Cancelled'
                                                            ? 'red'
                                                            : 'zinc'
                                                }
                                                className="text-end w-fit mb-3"
                                            >
                                                {bookingInfo.status?.description?.en}
                                            </Badge>
                                        </p>
                                        <div className="flex justify-end gap-2 mt-2">
                                            {bookingInfo?.status?.code === 'CNF' &&
                                                !isCheckInMode &&
                                                !isTravelDatePassed(travel_date) && (
                                                    <button
                                                        onClick={handleCancelBooking}
                                                        className="hover:bg-red/80 mt-0 bg-red  justify-center rounded-md px-3 py-2 w-auto text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                    >
                                                        {t('Cancel Booking')}
                                                    </button>
                                                )}

                                            {bookingInfo?.booking_type?.code === 'CBB' &&
                                                !isCheckInMode &&
                                                !isTravelDatePassed(travel_date) && (
                                                    <button
                                                        onClick={() => setOpenUpdateDialog(true)}
                                                        className="hover:bg-green/80 bg-green justify-center rounded-md px-3 py-2 w-auto text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                    >
                                                        {t('Update Booking')}
                                                    </button>
                                                )}

                                            {bookingInfo?.booking_type?.code === 'CBB' &&
                                                !isCheckInMode &&
                                                !isTravelDatePassed(travel_date) && (
                                                    <button
                                                        onClick={() => setOpenUpdateStatusDialog(true)}
                                                        className="hover:bg-primary/80 bg-primary justify-center rounded-md px-3 py-2 w-auto text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                    >
                                                        {t('Update Status')}
                                                    </button>
                                                )}
                                        </div>
                                    </div>
                                </div>

                                {/* Route Info */}
                                <div className="bg-[#F4F7F8] rounded-sm p-4 mb-4 ">
                                    {isDayPassBooking ? (
                                        // Day Pass Layout
                                        <div className="grid grid-cols-1 md:grid-cols-3 text-center text-sm gap-y-4 md:gap-y-0 text-gray-600">
                                            <div className="flex">
                                                <CalendarIcon className="w-5 h-5 mb-1 text-[#285f74]" />
                                                <div className="ps-3 text-start">
                                                    <p className="pb-1">{t('Travel Date')}</p>
                                                    <p className="font-bold text-[16px] break-words">
                                                        {customDateFormat(getDisplayTravelDate())}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex">
                                                <UserIcon className="w-5 h-5 mb-1 text-[#285f74]" />
                                                <div className="ps-3 text-start">
                                                    <p className="pb-1">{t('Total Passengers')}</p>
                                                    <p className="font-bold text-[16px] break-words">
                                                        {bookingInfo?.passengers?.length || 0} {t('Passengers')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex">
                                                <img
                                                    src={
                                                        bookingInfo?.passengers?.[0]?.seat_info?.seat_type_code ===
                                                        'VIP'
                                                            ? vipSeat
                                                            : stSeat
                                                    }
                                                    className="w-5 h-5"
                                                    alt="seat"
                                                />
                                                <div className="ps-3 text-start">
                                                    <p className="pb-1">{t('Seat Type')}</p>
                                                    <p className="font-bold text-[16px] break-words">
                                                        {SEAT_TYPES[
                                                            bookingInfo?.passengers?.[0]?.seat_info?.seat_type_code
                                                        ] ||
                                                            bookingInfo?.passengers?.[0]?.seat_info?.seat_type_code ||
                                                            'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : bookingInfo?.trip_segments && bookingInfo.trip_segments?.length > 1 ? (
                                        // Return Trip Layout
                                        <div className="space-y-4">
                                            {/* Departure Trip */}
                                            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 w-full">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="w-7 h-7 bg-primary/20 rounded-full flex items-center justify-center">
                                                        <span className="text-sm font-bold text-primary">1</span>
                                                    </div>
                                                    <div className="flex flex-col items-start">
                                                        <div className="flex items-center">
                                                            <BoatIcon className="w-5 h-5 fill-[#285f74]" />
                                                            <span className="ps-2 font-bold text-base">
                                                                {bookingInfo.trip_segments[0].start?.location?.name?.en}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-start ps-5">
                                                        <div className="flex items-center">
                                                            <BoatIcon className="w-5 h-5 fill-[#285f74]" />
                                                            <span className="ps-2 font-bold text-base">
                                                                {bookingInfo.trip_segments[0].end?.location?.name?.en}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <hr className="my-2" />
                                                <div className="flex flex-col md:flex-row items-center gap-3 text-gray-700 text-sm">
                                                    <div className="flex items-center">
                                                        <CalendarIcon className="w-5 h-5 text-[#285f74] mr-1" />
                                                        <span className="font-semibold">
                                                            {customDateFormat(bookingInfo.trip_segments[0].travel_date)}
                                                        </span>
                                                        <span className="mx-2">|</span>
                                                        <span className="font-semibold">
                                                            {customTimeFormat(
                                                                bookingInfo.trip_segments[0].start?.departure_time
                                                            )}
                                                        </span>
                                                        {bookingInfo.trip_segments[0].duration_minutes && (
                                                            <span className="ml-4 bg-gray-100 rounded-full px-2 py-1 text-xs text-primary">
                                                                Duration:{' '}
                                                                {formatDuration(
                                                                    bookingInfo.trip_segments[0].duration_minutes
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Return Trip */}
                                            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 w-full">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="w-7 h-7 bg-primary/20 rounded-full flex items-center justify-center">
                                                        <span className="text-sm font-bold text-primary">2</span>
                                                    </div>

                                                    {/* From */}
                                                    <div className="flex flex-col items-start">
                                                        <div className="flex items-center">
                                                            <BoatIcon className="w-5 h-5 fill-[#285f74]" />
                                                            <span className="ps-2 font-bold text-base">
                                                                {bookingInfo.trip_segments[1].start?.location?.name?.en}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* To */}
                                                    <div className="flex flex-col items-start ps-5">
                                                        <div className="flex items-center">
                                                            <BoatIcon className="w-5 h-5 fill-[#285f74]" />
                                                            <span className="ps-2 font-bold text-base">
                                                                {bookingInfo.trip_segments[1].end?.location?.name?.en}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <hr className="my-2" />

                                                {/* Date / Time / Duration */}
                                                <div className="flex flex-col md:flex-row items-center gap-3 text-gray-700 text-sm">
                                                    <div className="flex items-center">
                                                        <CalendarIcon className="w-5 h-5 text-[#285f74] mr-1" />
                                                        <span className="font-semibold">
                                                            {customDateFormat(bookingInfo.trip_segments[1].travel_date)}
                                                        </span>
                                                        <span className="mx-2">|</span>
                                                        <span className="font-semibold">
                                                            {customTimeFormat(
                                                                bookingInfo.trip_segments[1].start?.departure_time
                                                            )}
                                                        </span>

                                                        {bookingInfo.trip_segments[1].duration_minutes && (
                                                            <span className="ml-4 bg-gray-100 rounded-full px-2 py-1 text-xs text-primary">
                                                                Duration:{' '}
                                                                {formatDuration(
                                                                    bookingInfo.trip_segments[1].duration_minutes
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Single Trip Layout (Original)
                                        <div className="grid grid-cols-1 md:grid-cols-3 text-center text-sm gap-y-4 md:gap-y-0 text-gray-600">
                                            <div className="flex">
                                                <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                                <div className="ps-3 text-start">
                                                    <p className="pb-1">{t('From Location')}</p>
                                                    <p className="font-bold text-[16px] break-words">
                                                        {bookingInfo?.trip_segments?.[0]?.start?.location?.name?.en ||
                                                            t('N/A')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex">
                                                <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                                <div className="ps-3 text-start">
                                                    <p className="pb-1">{t('To Location')}</p>
                                                    <p className="font-bold text-[16px] break-words">
                                                        {bookingInfo?.trip_segments?.[0]?.end?.location?.name?.en ||
                                                            t('N/A')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex">
                                                <CalendarIcon className="w-5 h-5 mb-1 text-[#285f74]" />
                                                <div className="ps-3 text-start">
                                                    <p className="pb-1">{t('Travel Date')}</p>
                                                    <p className="font-bold text-[15px]">
                                                        {customDateFormat(
                                                            moment(
                                                                bookingInfo.metadata?.travel_date ||
                                                                    bookingInfo.trip_segments?.[0]?.travel_date,
                                                                ['DD MMM YYYY', 'YYYY-MM-DD']
                                                            ).format('YYYY-MM-DD')
                                                        )}{' '}
                                                    </p>
                                                    {bookingInfo.trip_segments?.[0]?.duration_minutes && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {t('Duration')}:{' '}
                                                            {formatDuration(
                                                                bookingInfo.trip_segments[0].duration_minutes
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">
                                    <div>
                                        <div className="text-gray-700 mb-2">
                                            <p className="font-md font-bold mb-4 border-b border-slate-100 pb-2">
                                                {t('Booking Information')}
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-0">
                                                <div className="flex flex-col justify-between gap-y-1">
                                                    <p className="text-sm text-gray-600">{t('Booking ID')}</p>
                                                    <p className="text-sm font-semibold text-gray-600">
                                                        {bookingInfo.booking_code}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col justify-between gap-y-1">
                                                    <p className="text-sm text-gray-600">{t('Booked By')}</p>
                                                    <p className="text-sm font-semibold text-gray-600 break-words">
                                                        {bookingInfo.user
                                                            ? bookingInfo.user?.user_info?.given_name
                                                            : bookingInfo.user_id}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col justify-between gap-y-1">
                                                    <p className="text-sm text-gray-600">{t('Phone Number')}</p>
                                                    <p className="text-sm font-semibold text-gray-600">
                                                        {bookingInfo.user?.user_info ? (
                                                            bookingInfo.user?.user_info?.phone_number
                                                        ) : (
                                                            <span className="italic text-gray-300"> N/A</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col justify-between gap-y-1">
                                                    <p className="text-sm text-gray-600">{t('Email')}</p>
                                                    <p className="text-sm font-semibold break-all text-gray-600">
                                                        {bookingInfo?.user ? (
                                                            bookingInfo.user?.email
                                                        ) : (
                                                            <span className="italic text-gray-300"> N/A</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col justify-between gap-y-1">
                                                    <p className="text-sm text-gray-600">{t('Created On')}</p>
                                                    <p className="text-sm font-semibold text-gray-600">
                                                        {bookingInfo?.user_audit?.created_date ? (
                                                            customDateTimeFormat(bookingInfo.user_audit.created_date)
                                                        ) : (
                                                            <span className="italic text-gray-300"> N/A</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg border-[1px] border-solid border-slate-300 p-4 mt-3">
                                <div className="text-gray-700">
                                    <div className="flex justify-between border-b border-slate-200 items-center w-full pb-2 mb-3">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-base font-bold text-gray-900">{t('Passenger Info')}</h3>
                                            {(bookingInfo?.trip_segments || isDayPassBooking) && (
                                                <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                                    <UserIcon className="w-5 h-5" />
                                                    <span className="font-medium">
                                                        {isDayPassBooking
                                                            ? `${bookingInfo?.passengers?.length || 0} ${t('Passenger')}${(bookingInfo?.passengers?.length || 0) > 1 ? 's' : ''}`
                                                            : `${bookingInfo.trip_segments.reduce((total, segment) => total + segment.passengers.length, 0)} ${t('Passenger')}${bookingInfo.trip_segments.reduce((total, segment) => total + segment.passengers.length, 0) > 1 ? 's' : ''}`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        {bookingInfo?.booking_type?.code != 'DYP' && !isCheckInMode && (
                                            <TicketActions bookingData={bookingInfo} />
                                        )}
                                    </div>

                                    {/* Passenger Summary */}
                                    {(bookingInfo?.trip_segments || isDayPassBooking) && (
                                        <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200">
                                            <h4 className="text-xs font-semibold text-gray-700 mb-2">
                                                {t('Booking Summary')}
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {(() => {
                                                    // Count seats - handle both trip_segments and Day Pass bookings
                                                    let seatCounts = {}

                                                    if (isDayPassBooking && bookingInfo?.passengers) {
                                                        // For Day Pass, count from booking-level passengers
                                                        bookingInfo.passengers.forEach(person => {
                                                            const seatType = person.seat_info?.seat_type_code || 'STD'
                                                            seatCounts[seatType] = (seatCounts[seatType] || 0) + 1
                                                        })
                                                    } else if (bookingInfo?.trip_segments) {
                                                        // Count seats across all trip segments
                                                        seatCounts = bookingInfo.trip_segments.reduce(
                                                            (acc, segment) => {
                                                                segment.passengers.forEach(person => {
                                                                    const seatType = person.seat_info.seat_type_code
                                                                    acc[seatType] = (acc[seatType] || 0) + 1
                                                                })
                                                                return acc
                                                            },
                                                            {}
                                                        )
                                                    }

                                                    return Object.entries(seatCounts).map(([seatType, count]) => (
                                                        <div
                                                            key={seatType}
                                                            className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs"
                                                        >
                                                            <img
                                                                src={seatType === 'VIP' ? vipSeat : stSeat}
                                                                className="w-5 h-5"
                                                                alt={seatType}
                                                            />
                                                            <span className="font-medium text-gray-700">
                                                                {count} {SEAT_TYPES[seatType]}
                                                                {count > 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                    ))
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* Individual Passengers */}
                                    {bookingInfo?.trip_segments && (
                                        <div className="space-y-3">
                                            {bookingInfo.trip_segments.map((segment, segmentIndex) => {
                                                // Check if any passenger in this segment is checked in
                                                const hasCheckedInPassengers = segment.passengers?.some(
                                                    p => p.check_in !== null && p.check_in !== undefined
                                                )

                                                return (
                                                    <div key={segment.segment_id} className="space-y-2">
                                                        {/* Check-in button for one-way trips */}
                                                        {bookingInfo.trip_segments.length === 1 &&
                                                            !isDomesticRoute &&
                                                            bookingInfo.status?.code !== 'CXL' &&
                                                            segment.trip_status !== 'CXL' &&
                                                            (segment.allow_check_in ||
                                                                (!segment.allow_check_in &&
                                                                    segment.passengers.some(
                                                                        p => p.check_in?.status?.code === 'CHK'
                                                                    ))) && (
                                                                <div className="flex items-center justify-end gap-2 mb-3">
                                                                    {segment.trip_status && (
                                                                        <Badge
                                                                            color={
                                                                                segment.trip_status === 'CNF'
                                                                                    ? 'emerald'
                                                                                    : segment.trip_status === 'CMP'
                                                                                      ? 'emerald'
                                                                                      : segment.trip_status === 'CXL'
                                                                                        ? 'red'
                                                                                        : segment.trip_status === 'TRC'
                                                                                          ? 'red'
                                                                                          : segment.trip_status ===
                                                                                              'RSC'
                                                                                            ? 'red'
                                                                                            : segment.trip_status ===
                                                                                                'NOS'
                                                                                              ? 'orange'
                                                                                              : segment.trip_status ===
                                                                                                  'PND'
                                                                                                ? 'yellow'
                                                                                                : segment.trip_status ===
                                                                                                    'PRC'
                                                                                                  ? 'yellow'
                                                                                                  : segment.trip_status ===
                                                                                                      'ONP'
                                                                                                    ? 'blue'
                                                                                                    : segment.trip_status ===
                                                                                                        'REV'
                                                                                                      ? 'orange'
                                                                                                      : 'zinc'
                                                                            }
                                                                        >
                                                                            {getTripStatusLabel(segment.trip_status)}
                                                                        </Badge>
                                                                    )}
                                                                    {segment.trip_status !== 'REV' && (
                                                                        <button
                                                                            onClick={() =>
                                                                                handleCheckIn(
                                                                                    segment.segment_id,
                                                                                    segment.travel_date
                                                                                )
                                                                            }
                                                                            className="flex items-center gap-1 px-4 py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-colors"
                                                                            style={{
                                                                                backgroundColor:
                                                                                    'rgb(40 95 116 / var(--tw-bg-opacity, 1))',
                                                                            }}
                                                                        >
                                                                            {segment.passengers.some(
                                                                                p => p.check_in?.status?.code === 'CHK'
                                                                            ) ? (
                                                                                <>
                                                                                    <EyeIcon className="w-4 h-4" />
                                                                                    {t('View Check-In')}
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <CheckIcon className="w-4 h-4" />
                                                                                    {t('Check-In')}
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        {/* Reschedule button for one-way trips (visible only if reschedule_allowed is true) */}
                                                        {bookingInfo.trip_segments.length === 1 &&
                                                            bookingInfo.status?.code !== 'CXL' &&
                                                            segment.trip_status !== 'CXL' &&
                                                            segment.reschedule_allowed && (
                                                                <div className="flex items-center justify-end gap-2 mb-3">
                                                                    <button
                                                                        onClick={() => handleReschedule(segment)}
                                                                        className="flex items-center gap-1 px-4 py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-colors bg-primary"
                                                                    >
                                                                        <ArrowPathIcon className="w-4 h-4" />
                                                                        {t('Reschedule')}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        {/* Segment Header */}
                                                        {bookingInfo.trip_segments.length > 1 && (
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                                                                    <span className="text-xs font-bold text-primary">
                                                                        {segmentIndex + 1}
                                                                    </span>
                                                                </div>
                                                                <h4 className="text-sm font-semibold text-gray-800">
                                                                    {segmentIndex === 0 ? t(' Trip') : t(' Trip')}
                                                                </h4>
                                                                <div className="flex-1 h-px bg-gray-300"></div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-500">
                                                                        {customDateFormat(segment.travel_date)}
                                                                    </span>
                                                                    {segment.trip_status && (
                                                                        <Badge
                                                                            color={
                                                                                segment.trip_status === 'CNF'
                                                                                    ? 'emerald'
                                                                                    : segment.trip_status === 'CMP'
                                                                                      ? 'emerald'
                                                                                      : segment.trip_status === 'CXL'
                                                                                        ? 'red'
                                                                                        : segment.trip_status === 'TRC'
                                                                                          ? 'red'
                                                                                          : segment.trip_status ===
                                                                                              'RSC'
                                                                                            ? 'red'
                                                                                            : segment.trip_status ===
                                                                                                'NOS'
                                                                                              ? 'orange'
                                                                                              : segment.trip_status ===
                                                                                                  'PND'
                                                                                                ? 'yellow'
                                                                                                : segment.trip_status ===
                                                                                                    'PRC'
                                                                                                  ? 'yellow'
                                                                                                  : segment.trip_status ===
                                                                                                      'ONP'
                                                                                                    ? 'blue'
                                                                                                    : 'zinc'
                                                                            }
                                                                        >
                                                                            {getTripStatusLabel(segment.trip_status)}
                                                                        </Badge>
                                                                    )}
                                                                    {/* Trip-level Check-in button */}
                                                                    {bookingInfo.status?.code !== 'CXL' &&
                                                                        segment.trip_status !== 'CXL' &&
                                                                        (segment.allow_check_in ||
                                                                            (!segment.allow_check_in &&
                                                                                segment.passengers.some(
                                                                                    p =>
                                                                                        p.check_in?.status?.code ===
                                                                                        'CHK'
                                                                                ))) &&
                                                                        !isDomesticRoute && (
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleCheckIn(
                                                                                        segment.segment_id,
                                                                                        segment.travel_date
                                                                                    )
                                                                                }
                                                                                className="flex items-center gap-1 px-3 py-1.5 text-white text-xs font-medium rounded-md hover:opacity-90 transition-colors"
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        'rgb(40 95 116 / var(--tw-bg-opacity, 1))',
                                                                                }}
                                                                            >
                                                                                {segment.passengers.some(
                                                                                    p =>
                                                                                        p.check_in?.status?.code ===
                                                                                        'CHK'
                                                                                ) ? (
                                                                                    <>
                                                                                        <EyeIcon className="w-4 h-4" />
                                                                                        {t('View Check-In')}
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <CheckIcon className="w-4 h-4" />
                                                                                        {t('Check-In')}
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    {/* Reschedule (trip-wise, only if reschedule_allowed) */}
                                                                    {bookingInfo.status?.code !== 'CXL' &&
                                                                        segment.trip_status !== 'CXL' &&
                                                                        segment.reschedule_allowed && (
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleReschedule(segment)
                                                                                }
                                                                                className="flex items-center gap-1 px-3 py-1.5 text-white text-xs font-medium rounded-md hover:opacity-90 transition-colors bg-primary"
                                                                            >
                                                                                <ArrowPathIcon className="w-4 h-4" />
                                                                                {t('Reschedule')}
                                                                            </button>
                                                                        )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Passengers for this segment */}
                                                        <div className="space-y-2">
                                                            {segment.passengers.map((person, idx) => (
                                                                <div
                                                                    key={person.passenger_id}
                                                                    className="bg-gray-50 rounded p-3 border border-gray-200"
                                                                >
                                                                    {/* Passenger Header */}
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                                                <UserIcon className="w-5 h-5 text-primary" />
                                                                            </div>
                                                                            <div className="min-w-0 flex-1">
                                                                                <h4 className="text-sm font-semibold text-gray-900 break-words">
                                                                                    {person.passenger.given_name}{' '}
                                                                                    {person.passenger.family_name}
                                                                                </h4>
                                                                                <p className="text-xs text-gray-600">
                                                                                    {AGE_GROUPS[
                                                                                        person.passenger.age_group_code
                                                                                    ] ||
                                                                                        person.passenger.age_group_code}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <p className="text-xs font-medium text-gray-600">
                                                                                {t('Ticket No')}
                                                                            </p>
                                                                            <p className="text-xs font-mono font-semibold text-primary">
                                                                                {person.ticket_number}
                                                                            </p>
                                                                            {/* Check-in status indicator */}
                                                                            {person.check_in && (
                                                                                <div className="mt-1">
                                                                                    <Badge
                                                                                        color={
                                                                                            person.check_in.status
                                                                                                ?.code === 'CHK'
                                                                                                ? 'green'
                                                                                                : person.check_in.status
                                                                                                        ?.code === 'CRT'
                                                                                                  ? 'yellow'
                                                                                                  : 'gray'
                                                                                        }
                                                                                        className="text-xs"
                                                                                    >
                                                                                        {person.check_in.status
                                                                                            ?.description?.en ||
                                                                                            person.check_in.status
                                                                                                ?.code ||
                                                                                            'Pending'}
                                                                                    </Badge>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Contact Information */}
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                                                        <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                                                            <PhoneIcon className="w-5 h-5 text-primary flex-shrink-0" />
                                                                            <div className="min-w-0 flex-1">
                                                                                <p className="text-xs text-gray-500">
                                                                                    {t('Phone')}
                                                                                </p>
                                                                                <p className="text-xs font-medium text-gray-900 break-all">
                                                                                    {person.passenger.phone_number || (
                                                                                        <span className="italic text-gray-400">
                                                                                            N/A
                                                                                        </span>
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                                                            <ChatBubbleBottomCenterIcon className="w-5 h-5 text-primary flex-shrink-0" />
                                                                            <div className="min-w-0 flex-1">
                                                                                <p className="text-xs text-gray-500">
                                                                                    {t('Email')}
                                                                                </p>
                                                                                <p className="text-xs font-medium text-gray-900 break-all">
                                                                                    {person.passenger.email || (
                                                                                        <span className="italic text-gray-400">
                                                                                            N/A
                                                                                        </span>
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Seat Information */}
                                                                    <div className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="flex items-center gap-1">
                                                                                <img
                                                                                    src={
                                                                                        person.seat_info
                                                                                            .seat_type_code === 'VIP'
                                                                                            ? vipSeat
                                                                                            : stSeat
                                                                                    }
                                                                                    className="w-5 h-5"
                                                                                    alt={
                                                                                        person.seat_info.seat_type_code
                                                                                    }
                                                                                />
                                                                                <span className="text-xs font-medium text-gray-700">
                                                                                    {
                                                                                        SEAT_TYPES[
                                                                                            person.seat_info
                                                                                                .seat_type_code
                                                                                        ]
                                                                                    }
                                                                                </span>
                                                                            </div>
                                                                            <div className="w-px h-4 bg-gray-300"></div>
                                                                            <div className="flex items-center gap-1">
                                                                                <div className="w-5 h-5 bg-gray-200 rounded flex items-center justify-center">
                                                                                    <span className="text-xs font-bold text-gray-700">
                                                                                        {person.seat_info.seat_no}
                                                                                    </span>
                                                                                </div>
                                                                                <span className="text-xs font-medium text-gray-700">
                                                                                    {t('Seat')}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="text-right">
                                                                                <p className="text-xs text-gray-500">
                                                                                    {t('Seat Type')}
                                                                                </p>
                                                                                <p className="text-xs font-semibold text-primary">
                                                                                    {person.seat_info.seat_type_code}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Medical Issue Section */}
                                                                    {person.passenger.medical_condition && (
                                                                        <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                                                                            <div className="flex items-start gap-3">
                                                                                <div className="flex-shrink-0">
                                                                                    <MedicalIcon className="h-5 w-5" />
                                                                                </div>
                                                                                <div>
                                                                                    <h5 className="text-sm font-medium text-red-800">
                                                                                        {t('Medical Condition')}
                                                                                    </h5>
                                                                                    <p className="text-sm text-red-700 mt-1">
                                                                                        {person.passenger
                                                                                            .medical_remarks ||
                                                                                            t(
                                                                                                'Medical condition reported'
                                                                                            )}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Document Attachments Section */}
                                                                    {person.passenger.document &&
                                                                        person.passenger.document.length > 0 && (
                                                                            <div className="mt-2 space-y-2">
                                                                                {person.passenger.document.map(
                                                                                    (doc, docIndex) => (
                                                                                        <div
                                                                                            key={docIndex}
                                                                                            className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200"
                                                                                        >
                                                                                            <PassportIcon className="w-5 h-5 text-primary flex-shrink-0" />
                                                                                            <div className="min-w-0 flex-1">
                                                                                                <p className="text-xs font-medium text-gray-900">
                                                                                                    {t(
                                                                                                        'Passport and Visa'
                                                                                                    )}
                                                                                                </p>
                                                                                                <p className="text-xs font-medium text-gray-500 truncate">
                                                                                                    {doc.file_name}
                                                                                                </p>
                                                                                                <p className="text-xs text-gray-500">
                                                                                                    {(
                                                                                                        doc.size /
                                                                                                        1024 /
                                                                                                        1024
                                                                                                    ).toFixed(2)}{' '}
                                                                                                    MB • {doc.mime_type}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <button
                                                                                                    onClick={() =>
                                                                                                        handleViewDocument(
                                                                                                            doc
                                                                                                        )
                                                                                                    }
                                                                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary hover:text-primary/80 hover:bg-gray-50 rounded transition-colors flex-shrink-0"
                                                                                                    title={t(
                                                                                                        'View Document'
                                                                                                    )}
                                                                                                >
                                                                                                    <EyeIcon className="h-4 w-4" />
                                                                                                    {t('View')}
                                                                                                </button>
                                                                                                <button
                                                                                                    onClick={() =>
                                                                                                        handleDownloadDocument(
                                                                                                            doc
                                                                                                        )
                                                                                                    }
                                                                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors flex-shrink-0"
                                                                                                    title={t(
                                                                                                        'Download Document'
                                                                                                    )}
                                                                                                >
                                                                                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                                                                                    {t('Download')}
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}

                                    {/* Day Pass Passengers */}
                                    {isDayPassBooking && bookingInfo?.passengers && (
                                        <div className="space-y-3">
                                            <div className="space-y-2">
                                                {bookingInfo.passengers.map((person, idx) => (
                                                    <div
                                                        key={person.passenger_id}
                                                        className="bg-gray-50 rounded p-3 border border-gray-200"
                                                    >
                                                        {/* Passenger Header */}
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                                    <UserIcon className="w-5 h-5 text-primary" />
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-sm font-semibold text-gray-900 break-words">
                                                                        {person.passenger.given_name}{' '}
                                                                        {person.passenger.family_name}
                                                                    </h4>
                                                                    <p className="text-xs text-gray-600">
                                                                        {AGE_GROUPS[person.passenger.age_group_code] ||
                                                                            person.passenger.age_group_code}
                                                                        {person.passenger.special_needs && (
                                                                            <span className="ml-2 text-orange-600 font-medium">
                                                                                • {t('Special Needs')}
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-xs font-medium text-gray-600">
                                                                    {t('Ticket No')}
                                                                </p>
                                                                <p className="text-xs font-mono font-semibold text-primary">
                                                                    {person.ticket_number}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Contact Information */}
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                                            <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                                                <PhoneIcon className="w-5 h-5 text-primary flex-shrink-0" />
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-xs text-gray-500">
                                                                        {t('Phone')}
                                                                    </p>
                                                                    <p className="text-xs font-medium text-gray-900 break-all">
                                                                        {person.passenger.phone_number || (
                                                                            <span className="italic text-gray-400">
                                                                                N/A
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                                                <ChatBubbleBottomCenterIcon className="w-5 h-5 text-primary flex-shrink-0" />
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-xs text-gray-500">
                                                                        {t('Email')}
                                                                    </p>
                                                                    <p className="text-xs font-medium text-gray-900 break-all">
                                                                        {person.passenger.email || (
                                                                            <span className="italic text-gray-400">
                                                                                N/A
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Seat Information */}
                                                        <div className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex items-center gap-1">
                                                                    <img
                                                                        src={
                                                                            person.seat_info?.seat_type_code === 'VIP'
                                                                                ? vipSeat
                                                                                : stSeat
                                                                        }
                                                                        className="w-5 h-5"
                                                                        alt={person.seat_info?.seat_type_code}
                                                                    />
                                                                    <span className="text-xs font-medium text-gray-700">
                                                                        {SEAT_TYPES[person.seat_info?.seat_type_code] ||
                                                                            person.seat_info?.seat_type_code}
                                                                    </span>
                                                                </div>
                                                                <div className="w-px h-4 bg-gray-300"></div>
                                                                <div className="flex items-center gap-1">
                                                                    <div className="w-5 h-5 bg-gray-200 rounded flex items-center justify-center">
                                                                        <span className="text-xs font-bold text-gray-700">
                                                                            {person.seat_info?.seat_no || '-'}
                                                                        </span>
                                                                    </div>
                                                                    <span className="text-xs font-medium text-gray-700">
                                                                        {t('Seat')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <div className="text-right">
                                                                    <p className="text-xs text-gray-500">
                                                                        {t('Travel Date')}
                                                                    </p>
                                                                    <p className="text-xs font-semibold text-primary">
                                                                        {customDateFormat(person.travel_date)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Shuttle Service Details */}
                                    {bookingInfo?.trip_segments && (
                                        <div className="w-full mt-3">
                                            {bookingInfo.trip_segments.some(
                                                segment => segment.shuttle_service && segment.shuttle_service.length > 0
                                            ) && (
                                                <>
                                                    <h4 className="text-sm font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-1">
                                                        {t('Shuttle Service')}
                                                    </h4>
                                                    <div className="space-y-2">
                                                        {bookingInfo.trip_segments.map(
                                                            (segment, segmentIndex) =>
                                                                segment.shuttle_service &&
                                                                segment.shuttle_service.length > 0 && (
                                                                    <div
                                                                        key={segment.segment_id}
                                                                        className="bg-gray-50 rounded p-3 border border-gray-200"
                                                                    >
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
                                                                                <svg
                                                                                    className="w-3 h-3 text-white"
                                                                                    fill="#196CA2"
                                                                                    viewBox="0 0 20 20"
                                                                                >
                                                                                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                                                                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z" />
                                                                                </svg>
                                                                            </div>
                                                                            <h5 className="text-xs font-medium text-gray-800">
                                                                                {segmentIndex === 0
                                                                                    ? t('Trip')
                                                                                    : t('Trip')}{' '}
                                                                                -{' '}
                                                                                {customDateFormat(segment.travel_date)}
                                                                            </h5>
                                                                        </div>
                                                                        {segment.shuttle_service.map(
                                                                            (service, serviceIndex) => (
                                                                                <div
                                                                                    key={serviceIndex}
                                                                                    className="bg-white rounded p-3 border border-gray-100 mb-2 last:mb-0"
                                                                                >
                                                                                    <div className="flex justify-between items-start">
                                                                                        <div className="flex-1">
                                                                                            <h6 className="text-sm font-semibold text-gray-900">
                                                                                                {service.provider_name}
                                                                                            </h6>
                                                                                            <p className="text-xs text-gray-600 mt-1">
                                                                                                {service.description_in_english ||
                                                                                                    service.description_in_arabic ||
                                                                                                    'Shuttle service'}
                                                                                            </p>
                                                                                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                                                                <span>
                                                                                                    {
                                                                                                        service.no_of_passenger
                                                                                                    }{' '}
                                                                                                    {service.no_of_passenger ===
                                                                                                    1
                                                                                                        ? t('passenger')
                                                                                                        : t(
                                                                                                              'passengers'
                                                                                                          )}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="text-right ml-4">
                                                                                            <span className="text-sm font-semibold text-gray-900">
                                                                                                {service.price?.price_with_tax?.toFixed(
                                                                                                    2
                                                                                                )}{' '}
                                                                                                BHD
                                                                                            </span>
                                                                                            {service.price?.tax > 0 && (
                                                                                                <p className="text-xs text-gray-500">
                                                                                                    {t('Incl. tax')}:{' '}
                                                                                                    {service.price.tax}{' '}
                                                                                                    BHD
                                                                                                </p>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                )
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Meal Summary for International Trips */}
                                    {bookingInfo?.trip_segments &&
                                        bookingInfo?.trip_segments[0]?.trip_meals_summary &&
                                        bookingInfo?.trip_segments[0]?.trip_meals_summary.length > 0 && (
                                            <div className="w-full mt-3">
                                                <h4 className="text-sm font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-1">
                                                    {t('Ordered Meals')}
                                                </h4>
                                                <div className="space-y-2">
                                                    {bookingInfo.trip_segments.map(
                                                        (segment, segmentIndex) =>
                                                            segment.trip_meals_summary &&
                                                            segment.trip_meals_summary.length > 0 && (
                                                                <div
                                                                    key={segment.segment_id}
                                                                    className="bg-gray-50 rounded p-2"
                                                                >
                                                                    <h5 className="text-xs font-medium text-gray-800 mb-2">
                                                                        {bookingInfo.trip_segments.length > 1
                                                                            ? `${t('Segment')} ${segment.segment_order} - ${customDateFormat(segment.travel_date)}`
                                                                            : `${t('Meals')} - ${customDateFormat(segment.travel_date)}`}
                                                                    </h5>
                                                                    <div className="space-y-1">
                                                                        {segment.trip_meals_summary.map(
                                                                            (meal, mealIndex) => (
                                                                                <div
                                                                                    key={`${segment.segment_id}-${meal.meal_id}-${mealIndex}`}
                                                                                    className="flex justify-between items-center bg-white rounded p-2 border border-gray-200"
                                                                                >
                                                                                    <div className="flex-1">
                                                                                        <h6 className="text-xs font-medium text-gray-900">
                                                                                            {meal.meal_name_en}
                                                                                        </h6>
                                                                                        {meal.meal_name_ar &&
                                                                                            meal.meal_name_ar !==
                                                                                                meal.meal_name_en && (
                                                                                                <p className="text-xs text-gray-600 mt-1">
                                                                                                    {meal.meal_name_ar}
                                                                                                </p>
                                                                                            )}
                                                                                    </div>
                                                                                    <div className="flex items-center space-x-1">
                                                                                        <span className="text-xs text-gray-500">
                                                                                            {t('Quantity')}:
                                                                                        </span>
                                                                                        <span className="font-semibold text-primary bg-primary/10 px-1 py-0.5 rounded text-xs">
                                                                                            {meal.meal_count}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>
                            {!isCheckInMode && (
                                <div className="bg-white rounded-lg border-[1px] border-solid border-slate-300 p-6 mt-5 pt-2 pb-0">
                                    <div className="text-gray-700 mt-2">
                                        <div className="flex justify-between border-b border-slate-100 items-center w-full pb-2">
                                            <p className="font-md font-bold ">{t('Payment Info')}</p>
                                            {bookingInfo?.status?.code === 'CNF' && bookingInfo.user_id == 'Admin' && (
                                                <button
                                                    type="button"
                                                    className={`flex items-center underline gap-x-1 py-1.5 text-sm font-bold leading-6 text-primary`}
                                                    onClick={() => setShowPaymentDialog(true)}
                                                >
                                                    <PlusCircleIcon aria-hidden="true" className="h-5 w-5" />
                                                    {t('Add Payment Info')}
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex flex-row gap-6">
                                            <ul className="w-full">
                                                {bookingInfo.metadata?.booking_type?.code != 'DYP' && (
                                                    <li className="my-4 px-1">
                                                        <div className="flex gap-4 w-full justify-between">
                                                            <p className="text-sm text-gray-600">Receipt No</p>
                                                            <p className="text-[14px] font-[500] lg:text-end">
                                                                {bookingInfo.receipt?.no ?? (
                                                                    <span className="italic font-semibold text-gray-300">
                                                                        {' '}
                                                                        N/A
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </li>
                                                )}
                                                <li className="my-4 px-1">
                                                    {bookingInfo.metadata?.booking_type?.code === 'DYP' && (
                                                        <>
                                                            <div className="flex gap-4 w-full justify-between">
                                                                <p className="text-sm text-gray-600">Booking Type</p>
                                                                <p className="text-[16px] font-bold text-primary lg:text-end">
                                                                    {
                                                                        bookingInfo.metadata?.booking_type.description[
                                                                            i18n?.language || 'en'
                                                                        ]
                                                                    }
                                                                </p>
                                                            </div>
                                                            <div className="flex gap-4 w-full justify-between mt-4">
                                                                <p className="text-sm text-gray-600">Booking Code</p>
                                                                <p
                                                                    className="text-[16px] font-bold text-primary underline cursor-pointer lg:text-end"
                                                                    onClick={() =>
                                                                        GoDetailsPage(
                                                                            bookingInfo.metadata?.booking_id,
                                                                            bookingInfo.metadata?.travel_date
                                                                        )
                                                                    }
                                                                >
                                                                    {bookingInfo.metadata?.booking_code}
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}
                                                </li>
                                                {bookingInfo.metadata?.booking_type?.code != 'DYP' && (
                                                    <>
                                                        {' '}
                                                        <li className="my-4 px-1">
                                                            <div className="flex gap-4 w-full justify-between">
                                                                <p className="text-sm text-gray-600">Date of Booking</p>
                                                                <p className="text-[14px] font-[500] lg:text-end">
                                                                    {bookingInfo.receipt?.date ? (
                                                                        customDateFormat(bookingInfo.receipt?.date)
                                                                    ) : (
                                                                        <span className="italic font-semibold text-gray-300">
                                                                            {' '}
                                                                            N/A
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </li>
                                                        <li className="my-4 px-1">
                                                            <div className="flex gap-4 w-full justify-between">
                                                                <p className="text-sm text-gray-600">Payment Time</p>
                                                                <p className="text-[14px] font-[500] lg:text-end">
                                                                    {bookingInfo.receipt?.date ? (
                                                                        customDateTimeFormat(bookingInfo.receipt?.time)
                                                                    ) : (
                                                                        <span className="italic font-semibold text-gray-300">
                                                                            {' '}
                                                                            N/A
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </li>
                                                    </>
                                                )}
                                                {bookingInfo.metadata?.booking_type?.code != 'DYP' && (
                                                    <li className="my-4 px-1">
                                                        <div className="flex gap-4 w-full justify-between">
                                                            <p className="text-sm text-gray-600">Payment Type</p>
                                                            <p className="text-[14px] font-[500] lg:text-end">
                                                                {bookingInfo.receipt?.card_brand || (
                                                                    <span className="italic font-semibold text-gray-300">
                                                                        N/A
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </li>
                                                )}
                                                <li className="my-4 px-1">
                                                    <div className="flex gap-4 w-full justify-between">
                                                        <p className="text-sm text-gray-600">Card No (Last 4 Digit)</p>
                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                            {(bookingInfo.receipt?.card_no
                                                                ? '**** **** **** ' + bookingInfo.receipt?.card_no
                                                                : '') || (
                                                                <span className="italic font-semibold text-gray-300">
                                                                    N/A
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Total Section */}
                                    <div className="pt-1 pb-0 ml-[-23px] mr-[-23px]">
                                        <div className="bg-[#F4F7F8] text-black py-3 px-4 rounded-lg flex justify-between items-center mt-[-5px]">
                                            <p className="font-semibold ps-3">{t('Total Amount')}</p>
                                            <p className="font-bold text-[19px] pe-3">
                                                {bookingInfo.price?.final_price || bookingInfo.total_price
                                                    ? Number(
                                                          bookingInfo.price?.final_price || bookingInfo.total_price
                                                      ).toFixed(2)
                                                    : '0.00'}{' '}
                                                {bookingInfo.receipt?.currency || 'BHD'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Refund Details Section - Show for cancelled trips */}
                            {bookingInfo?.trip_segments &&
                                bookingInfo.trip_segments.some(
                                    segment =>
                                        (segment.trip_status === 'CXL' || segment.trip_status === 'TRC') &&
                                        segment.refund?.segment_id
                                ) && (
                                    <div className="bg-white rounded-lg border-[1px] border-solid border-slate-300 p-6 mt-5 pt-2 pb-0">
                                        <div className="text-gray-700 mt-2">
                                            <div className="flex justify-between border-b border-slate-100 items-center w-full pb-2">
                                                <p className="font-md font-bold">{t('Refund Details')}</p>
                                            </div>

                                            {bookingInfo.trip_segments.map((segment, segmentIndex) => {
                                                // Only show refund details for cancelled trips with valid refund data
                                                if (
                                                    (segment.trip_status !== 'CXL' && segment.trip_status !== 'TRC') ||
                                                    !segment.refund?.segment_id
                                                ) {
                                                    return null
                                                }

                                                const refund = segment.refund

                                                return (
                                                    <div key={segment.segment_id}>
                                                        {/* Trip Header for multi-segment bookings */}
                                                        {bookingInfo.trip_segments.length > 1 && (
                                                            <div className="flex items-center gap-2 mt-4 mb-2">
                                                                <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                                                                    <span className="text-xs font-bold text-red-600">
                                                                        {segmentIndex + 1}
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm font-semibold text-gray-800">
                                                                    {segmentIndex === 0 ? t('Trip') : t('Trip')}
                                                                </p>
                                                                <Badge color="red" className="ml-auto">
                                                                    {getTripStatusLabel(segment.trip_status)}
                                                                </Badge>
                                                            </div>
                                                        )}

                                                        <div className="flex flex-row gap-6">
                                                            <ul className="w-full">
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Trip Price')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.trip_price?.toFixed(2) || '0.00'}{' '}
                                                                            BHD
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Refundable Base')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.refundable_base?.toFixed(2) ||
                                                                                '0.00'}{' '}
                                                                            BHD
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Policy Applied')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.policy_applied?.replace(
                                                                                /_/g,
                                                                                ' '
                                                                            ) || (
                                                                                <span className="italic font-semibold text-gray-300">
                                                                                    N/A
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Policy Percent')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.policy_percent != null ? (
                                                                                `${refund.policy_percent}%`
                                                                            ) : (
                                                                                <span className="italic font-semibold text-gray-300">
                                                                                    N/A
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Penalty Amount')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end text-orange-600">
                                                                            {refund.penalty_amount?.toFixed(2) ||
                                                                                '0.00'}{' '}
                                                                            BHD
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Cancelled At')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.cancelled_at ? (
                                                                                customDateTimeFormat(
                                                                                    refund.cancelled_at
                                                                                )
                                                                            ) : (
                                                                                <span className="italic font-semibold text-gray-300">
                                                                                    N/A
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Trip Departure Time')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.trip_departure_time ? (
                                                                                customDateTimeFormat(
                                                                                    refund.trip_departure_time
                                                                                )
                                                                            ) : (
                                                                                <span className="italic font-semibold text-gray-300">
                                                                                    N/A
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                <li className="my-4 px-1">
                                                                    <div className="flex gap-4 w-full justify-between">
                                                                        <p className="text-sm text-gray-600">
                                                                            {t('Hours Before Trip')}
                                                                        </p>
                                                                        <p className="text-[14px] font-[500] lg:text-end">
                                                                            {refund.hours_before_trip != null ? (
                                                                                `${refund.hours_before_trip.toFixed(2)} ${t('hours')}`
                                                                            ) : (
                                                                                <span className="italic font-semibold text-gray-300">
                                                                                    N/A
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </li>
                                                                {refund.remarks && (
                                                                    <li className="my-4 px-1">
                                                                        <div className="flex gap-4 w-full justify-between">
                                                                            <p className="text-sm text-gray-600">
                                                                                {t('Remarks')}
                                                                            </p>
                                                                            <p className="text-[14px] font-[500] lg:text-end text-primary">
                                                                                {refund.remarks}
                                                                            </p>
                                                                        </div>
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </div>

                                                        {/* Refund Amount Total Section */}
                                                        <div className="pt-1 pb-0 ml-[-23px] mr-[-23px]">
                                                            <div className="bg-[#F4F7F8] text-black py-3 px-4 rounded-lg flex justify-between items-center mt-[-5px]">
                                                                <p className="font-semibold ps-3 text-green-700">
                                                                    {t('Refund Amount')}
                                                                </p>
                                                                <p className="font-bold text-[19px] pe-3 text-green-700">
                                                                    {refund.refund_amount?.toFixed(2) || '0.00'} BHD
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                        </div>
                        {bookingInfo?.trip_segments && bookingInfo?.trip_segments[0]?.trip_locations.length > 0 && (
                            <div className="col-span-2 bg-white rounded-lg border-[1px] border-solid border-slate-300 p-6 mt-5 h-fit">
                                <div>
                                    <p className="font-md font-bold mb-4 border-b border-slate-100 pb-2">
                                        {t('Trip Info')}
                                    </p>

                                    {bookingInfo?.trip_segments && bookingInfo.trip_segments.length > 1 ? (
                                        // Return Trip Layout
                                        <div className="space-y-6">
                                            {/* Departure Trip Info */}
                                            <div>
                                                <div className="flex items-center gap-2 mb-4">
                                                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                                                        <span className="text-xs font-bold text-primary">1</span>
                                                    </div>
                                                    <h4 className="text-sm font-semibold text-gray-800">
                                                        {t('Departure Trip')}
                                                    </h4>
                                                    <div className="flex-1 h-px bg-gray-300"></div>
                                                    <span className="text-xs text-gray-500">
                                                        {customDateFormat(bookingInfo.trip_segments[0].travel_date)}
                                                    </span>
                                                </div>
                                                <div className="flow-root mt-4">
                                                    <ul className="-mb-8">
                                                        {bookingInfo.trip_segments[0]?.trip_locations.map(
                                                            (sch, scheduleIdx) => (
                                                                <li key={sch.location_id}>
                                                                    <div className="relative pb-4">
                                                                        {scheduleIdx !==
                                                                        (
                                                                            bookingInfo.trip_segments[0]
                                                                                ?.trip_locations ?? []
                                                                        ).length -
                                                                            1 ? (
                                                                            <span
                                                                                aria-hidden="true"
                                                                                className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                            />
                                                                        ) : null}
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'bg-white',
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-primary border-solid border-2  border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4 last:border-0">
                                                                                <div className="mt-[-5px]">
                                                                                    <p className="text-md font-medium text-gray-900 break-words">
                                                                                        {sch.location.name.en}
                                                                                    </p>
                                                                                    {scheduleIdx === 0 &&
                                                                                        bookingInfo.trip_segments[0]
                                                                                            ?.duration_minutes && (
                                                                                            <p className="flex text-sm font-medium text-gray-900 pt-2">
                                                                                                <InformationCircleIcon
                                                                                                    aria-hidden="true"
                                                                                                    className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                                />{' '}
                                                                                                {formatDuration(
                                                                                                    bookingInfo
                                                                                                        .trip_segments[0]
                                                                                                        ?.duration_minutes
                                                                                                )}{' '}
                                                                                            </p>
                                                                                        )}
                                                                                </div>
                                                                                <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                                                                    <time dateTime={''}>{''}</time>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>

                                            {/* Return Trip Info */}
                                            <div>
                                                <div className="flex items-center gap-2 mb-4">
                                                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                                                        <span className="text-xs font-bold text-primary">2</span>
                                                    </div>
                                                    <h4 className="text-sm font-semibold text-gray-800">
                                                        {t('Return Trip')}
                                                    </h4>
                                                    <div className="flex-1 h-px bg-gray-300"></div>
                                                    <span className="text-xs text-gray-500">
                                                        {customDateFormat(bookingInfo.trip_segments[1].travel_date)}
                                                    </span>
                                                </div>
                                                <div className="flow-root mt-4">
                                                    <ul className="-mb-8">
                                                        {bookingInfo.trip_segments[1]?.trip_locations.map(
                                                            (sch, scheduleIdx) => (
                                                                <li key={sch.location_id}>
                                                                    <div className="relative pb-4">
                                                                        {scheduleIdx !==
                                                                        (
                                                                            bookingInfo.trip_segments[1]
                                                                                ?.trip_locations ?? []
                                                                        ).length -
                                                                            1 ? (
                                                                            <span
                                                                                aria-hidden="true"
                                                                                className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                            />
                                                                        ) : null}
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'bg-white',
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-primary border-solid border-2  border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4 last:border-0">
                                                                                <div className="mt-[-5px]">
                                                                                    <p className="text-md font-medium text-gray-900 break-words">
                                                                                        {sch.location.name.en}
                                                                                    </p>
                                                                                    {scheduleIdx === 0 &&
                                                                                        bookingInfo.trip_segments[1]
                                                                                            ?.duration_minutes && (
                                                                                            <p className="flex text-sm font-medium text-gray-900 pt-2">
                                                                                                <InformationCircleIcon
                                                                                                    aria-hidden="true"
                                                                                                    className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                                />{' '}
                                                                                                {formatDuration(
                                                                                                    bookingInfo
                                                                                                        .trip_segments[1]
                                                                                                        ?.duration_minutes
                                                                                                )}{' '}
                                                                                            </p>
                                                                                        )}
                                                                                </div>
                                                                                <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                                                                    <time dateTime={''}>{''}</time>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Single Trip Layout (Original)
                                        <div className="flow-root mt-4">
                                            <ul className="-mb-8">
                                                {bookingInfo.trip_segments[0]?.trip_locations.map(
                                                    (sch, scheduleIdx) => (
                                                        <li key={sch.location_id}>
                                                            <div className="relative pb-4">
                                                                {scheduleIdx !==
                                                                (bookingInfo.trip_segments[0]?.trip_locations ?? [])
                                                                    .length -
                                                                    1 ? (
                                                                    <span
                                                                        aria-hidden="true"
                                                                        className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                    />
                                                                ) : null}
                                                                <div className="relative flex space-x-3">
                                                                    <div>
                                                                        <span
                                                                            className={classNames(
                                                                                'bg-white',
                                                                                'flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-primary border-solid border-2  border-primary'
                                                                            )}
                                                                        ></span>
                                                                    </div>
                                                                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4 last:border-0">
                                                                        <div className="mt-[-5px]">
                                                                            <p className="text-md font-medium text-gray-900">
                                                                                {sch.location.name.en}
                                                                            </p>
                                                                            {scheduleIdx === 0 &&
                                                                                bookingInfo.trip_segments[0]
                                                                                    ?.duration_minutes && (
                                                                                    <p className="flex text-sm font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />{' '}
                                                                                        {formatDuration(
                                                                                            bookingInfo.trip_segments[0]
                                                                                                ?.duration_minutes
                                                                                        )}{' '}
                                                                                    </p>
                                                                                )}
                                                                        </div>
                                                                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                                                            <time dateTime={''}>{''}</time>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* <h3 id="manage_page_title" className="text-lg font-bold leading-7 text-primary pb-1">
                        {t('Booking tickets')}
                    </h3>
                    <div className="bg-white rounded-lg shadow-lg border-[1px] border-solid border-[#D6D6D6] p-6 pt-2 mt-5 mb-8 print:p-0 print:m-0 print:shadow-none print:border-none print:rounded-none">
                        <BoatBookingConformation bookingData={bookingInfo} isManagePage={true} />
                    </div> */}
                </>
            )}

            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={confirmDialogTitle}
                    negativeActionText={t('Cancel')}
                    positiveActionText={t('Yes')}
                    message={confirmDialogMessage}
                    onPositive={() => {
                        setShowConfirmDialog(false)
                        cancelBooking({
                            variables: {
                                bookingId: booking_id,
                                cancellationType: cancellationType,
                                tripIds: cancellationType === 'PARTIAL' ? selectedTripIds : [],
                            },
                        })
                    }}
                    onNegative={() => {
                        setShowConfirmDialog(false)
                    }}
                />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title="Success"
                    message={dialogMessages}
                    onClose={() => setShowSucDialog(false)}
                />
            )}
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title="Error"
                    message={dialogMessages}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
            <PaymentInfoDialog
                open={showPaymentDialog}
                onClose={() => setShowPaymentDialog(false)}
                bookingData={bookingInfo}
                onSuccess={handlePaymentSuccess}
            />

            {/* Cancellation Type Selection Dialog */}
            {showCancellationDialog && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-md">
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">{t('Select Cancellation Type')}</h3>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-gray-600"
                                    onClick={() => setShowCancellationDialog(false)}
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="px-6 py-6">
                                <p className="text-sm text-gray-600 mb-6">
                                    {t('This booking has multiple trips. Choose how you want to cancel:')}
                                </p>

                                {/* Cancellation Type Selection */}
                                <div className="space-y-4 mb-6">
                                    <label className="flex items-start cursor-pointer">
                                        <input
                                            type="radio"
                                            name="cancellationType"
                                            value="FULL"
                                            checked={cancellationType === 'FULL'}
                                            onChange={e => handleCancellationTypeChange(e.target.value)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 mt-0.5"
                                        />
                                        <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">
                                                {t('Cancel Entire Booking')}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {t('Cancel all trips in this booking')}
                                            </div>
                                        </div>
                                    </label>
                                    <label className="flex items-start cursor-pointer">
                                        <input
                                            type="radio"
                                            name="cancellationType"
                                            value="PARTIAL"
                                            checked={cancellationType === 'PARTIAL'}
                                            onChange={e => handleCancellationTypeChange(e.target.value)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 mt-0.5"
                                        />
                                        <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">
                                                {t('Cancel Selected Trips')}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {t('Choose specific trips to cancel')}
                                            </div>
                                        </div>
                                    </label>
                                </div>

                                {/* Trip Selection for Partial Cancellation */}
                                {cancellationType === 'PARTIAL' && (
                                    <div className="border-t border-gray-200 pt-6">
                                        <h4 className="text-sm font-medium text-gray-900 mb-4">
                                            {t('Select trips to cancel:')}
                                        </h4>
                                        <div className="space-y-3 max-h-60 overflow-y-auto">
                                            {bookingInfo?.trip_segments?.map((segment, index) => (
                                                <div
                                                    key={segment.trip_id}
                                                    className={`flex items-start p-3 rounded-lg border border-gray-200 ${
                                                        segment.trip_status === 'CXL'
                                                            ? 'bg-gray-100 cursor-not-allowed opacity-60'
                                                            : 'hover:bg-gray-50 cursor-pointer'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTripIds.includes(segment.trip_id)}
                                                        onChange={() => handleTripSelection(segment.trip_id)}
                                                        disabled={segment.trip_status === 'CXL'}
                                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    />
                                                    <div className="ml-3 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-900">
                                                                {t('Trip')} {segment.segment_order}
                                                            </span>
                                                            {segment.trip_status === 'CXL' && (
                                                                <Badge color="red" className="text-xs">
                                                                    {t('Cancelled')}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {customDateFormat(segment.travel_date)} -{' '}
                                                            {segment.start.location.name.en} to{' '}
                                                            {segment.end.location.name.en}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {(() => {
                                            const availableTrips =
                                                bookingInfo?.trip_segments?.filter(
                                                    segment => segment.trip_status !== 'CXL'
                                                ) || []
                                            const hasSelectedTrips = selectedTripIds.length > 0
                                            const hasAvailableTrips = availableTrips.length > 0

                                            if (!hasSelectedTrips && hasAvailableTrips) {
                                                return (
                                                    <p className="text-xs text-red-500 mt-3 flex items-center">
                                                        <XMarkIcon className="h-4 w-4 mr-1" />
                                                        {t('Please select at least one trip to cancel')}
                                                    </p>
                                                )
                                            }
                                            return null
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    onClick={() => setShowCancellationDialog(false)}
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
                                    style={{ backgroundColor: 'rgb(40 95 116 / var(--tw-bg-opacity, 1))' }}
                                    onClick={proceedWithCancellation}
                                    disabled={
                                        cancellationType === 'PARTIAL' &&
                                        (() => {
                                            const availableTrips =
                                                bookingInfo?.trip_segments?.filter(
                                                    segment => segment.trip_status !== 'CXL'
                                                ) || []
                                            return availableTrips.length > 0 && selectedTripIds.length === 0
                                        })()
                                    }
                                >
                                    {t('Proceed')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <UpdateBoatDialog
                status={openUpdateDialog}
                title={t('Update Charter Booking')}
                booking={bookingInfo}
                onClose={() => setOpenUpdateDialog(false)}
                onSuccess={updatedBooking => {
                    setOpenUpdateDialog(false)
                    const updatedTravelDate = updatedBooking.trip_segments[0].travel_date
                    navigate(`/manage-boat-booking/details/${updatedBooking.booking_id}/${updatedTravelDate}`)
                    searchBookings()
                }}
            />

            <UpdateBoatStatusDialog
                status={openUpdateStatusDialog}
                title={t('Update Charter Boat Booking Status')}
                booking={bookingInfo}
                onClose={() => setOpenUpdateStatusDialog(false)}
                onSuccess={updatedBooking => {
                    setOpenUpdateStatusDialog(false)
                    searchBookings()
                }}
            />

            {/* Document Preview Dialog */}
            {showDocumentPreview && previewDocument && (
                <div
                    className="fixed inset-0 z-50 overflow-y-auto"
                    aria-labelledby="modal-title"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={() => setShowDocumentPreview(false)}
                        ></div>

                        {/* Center modal */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                            {/* Header */}
                            <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <DocumentIcon className="h-6 w-6 text-primary" />
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                                            {t('Document Preview')}
                                        </h3>
                                        <p className="text-sm text-gray-500 truncate max-w-md">
                                            {previewDocument.file_name}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleDownloadDocument(previewDocument)}
                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                        {t('Download')}
                                    </button>
                                    <button
                                        onClick={() => setShowDocumentPreview(false)}
                                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="bg-gray-100 px-4 py-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                {previewDocument.mime_type?.startsWith('image/') ? (
                                    <div className="flex justify-center">
                                        <img
                                            src={documentUrl}
                                            alt={previewDocument.file_name}
                                            className="max-w-full h-auto rounded shadow-lg"
                                        />
                                    </div>
                                ) : previewDocument.mime_type === 'application/pdf' ? (
                                    <iframe
                                        src={documentUrl}
                                        className="w-full rounded shadow-lg"
                                        style={{ height: '65vh' }}
                                        title={previewDocument.file_name}
                                    />
                                ) : (
                                    <div className="text-center py-12">
                                        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                                            {t('Preview not available')}
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {t('This file type cannot be previewed. Please download to view.')}
                                        </p>
                                        <div className="mt-6">
                                            <button
                                                onClick={() => handleDownloadDocument(previewDocument)}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                style={{ backgroundColor: 'rgb(40 95 116 / var(--tw-bg-opacity, 1))' }}
                                            >
                                                <ArrowDownTrayIcon className="h-5 w-5" />
                                                {t('Download File')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-50 px-4 py-3 flex justify-end border-t border-gray-200">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    onClick={() => setShowDocumentPreview(false)}
                                >
                                    {t('Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
